/** Admin audit viewer (A-10) + KPI (A-02) + deletion review (A-14) + S-03 analytics. */

import { apiFetch, downloadFile } from "./client";

import type { DeletionRequest } from "./support";

// ----- Audit viewer (A-10) -----

/** Where an entry comes from: record changes, or the order / Rx logs merged into the timeline. */
export type AuditSource = "audit_event" | "order_status_history" | "order_handoff_log" | "prescription_audit";
export type AuditActorRole = "patient" | "doctor" | "pharmacist" | "admin" | "support" | "system";

export interface AuditEntry {
  /** "<source>:<row id>", unique across the timeline. */
  id: string;
  source: AuditSource;
  at: string;
  actor_user_id: string | null;
  actor_role: string | null;
  /** Who acted, by name, when the account is known. */
  actor_name?: string | null;
  entity_type: string;
  entity_id: string | null;
  /** The record as people know it: an order / Rx reference, a person's or medicine's name. */
  entity_label?: string | null;
  action: string;
  detail: Record<string, unknown>;
}

export interface AuditTimeline {
  data: AuditEntry[];
  count: number;
  /** Pass back as `cursor` for the next page; null once there is no more. */
  next_cursor?: string | null;
  has_more?: boolean;
}

/** The viewer's narrowing. `q`: a full ID, an order / Rx reference, or words (action, person's name). */
export interface AuditFilter {
  q?: string;
  entity_type?: string;
  entity_id?: string;
  actor_user_id?: string;
  actor_role?: AuditActorRole;
  source?: AuditSource;
  action_contains?: string;
  /** ISO date-times; one without an offset is read as IST. */
  since?: string;
  until?: string;
}

export interface AuditQuery extends AuditFilter {
  order?: "newest" | "oldest";
  cursor?: string;
  limit?: number;
}

/** The entity types audit rows exist for (ADM-06): the viewer's filter list. */
export const auditEntityTypes = (): Promise<{ entity_types: string[] }> => apiFetch("/api/v1/admin/audit/entity-types");

function auditParams(f: AuditFilter): URLSearchParams {
  const q = new URLSearchParams();
  if (f.q?.trim()) q.set("q", f.q.trim());
  if (f.entity_type) q.set("entity_type", f.entity_type);
  if (f.entity_id) q.set("entity_id", f.entity_id);
  if (f.actor_user_id) q.set("actor_user_id", f.actor_user_id);
  if (f.actor_role) q.set("actor_role", f.actor_role);
  if (f.source) q.set("source", f.source);
  if (f.action_contains) q.set("action_contains", f.action_contains);
  if (f.since) q.set("since", f.since);
  if (f.until) q.set("until", f.until);
  return q;
}

/** One page of the merged timeline; the next is asked for with the response's `next_cursor`. */
export const auditTimeline = (params: AuditQuery): Promise<AuditTimeline> => {
  const q = auditParams(params);
  if (params.order) q.set("order", params.order);
  if (params.cursor) q.set("cursor", params.cursor);
  if (params.limit) q.set("limit", String(params.limit));
  return apiFetch(`/api/v1/admin/audit?${q.toString()}`);
};

/** How many entries match, counted up to 10,000 (`capped` past that). */
export const auditCount = (filter: AuditFilter): Promise<{ count: number; capped: boolean }> =>
  apiFetch(`/api/v1/admin/audit/count?${auditParams(filter).toString()}`);

// ----- KPI dashboard (A-02) -----
//
// Every window is `days` whole IST days up to now (1 = today so far), compared with the same span
// just before it. Rankings count paid orders not since cancelled, refunded or returned.

export type SeriesBucket = "hour" | "day";

/** A period's headline figures (the dashboard shows the previous period's for comparison). */
export interface KpiTotals {
  orders_total: number;
  paid_orders: number;
  gross_revenue: string;
  refunded_amount: string;
  net_revenue: string;
  average_order_value: string;
  customers: number;
  new_customers: number;
  units_sold: number;
  payment_success_rate: number;
}

export interface KpiSeriesPoint {
  start: string; // the bucket's start (IST)
  orders: number; // placed in it
  paid_orders: number; // paid in it
  revenue: string; // what those paid orders came to
}

export interface KpiDashboard extends KpiTotals {
  window_days: number;
  period_start: string;
  period_end: string;
  previous_start: string;
  previous_end: string;
  orders_by_status: Record<string, number>;
  payment_attempts: number;
  funnel: Record<string, number>;
  previous: KpiTotals;
  series_bucket: SeriesBucket;
  series: KpiSeriesPoint[];
  fulfilment: {
    median_hours_to_price: number | null;
    median_hours_to_pay: number | null;
    median_hours_to_deliver: number | null;
  };
}

export const kpiDashboard = (days = 30): Promise<KpiDashboard> =>
  apiFetch(`/api/v1/admin/kpi?days=${days}`);

export type MedicineRankSort = "units" | "revenue" | "orders";

export interface RankedMedicine {
  rank: number;
  previous_rank: number | null; // null: not sold in the previous period
  medicine_id: string;
  name: string;
  brand: string | null;
  unit: string;
  units_sold: number;
  orders: number;
  customers: number;
  revenue: string; // incl. GST
  unit_share: number;
  revenue_share: number;
}

export interface TopMedicines {
  window_days: number;
  period_start: string;
  period_end: string;
  sort: MedicineRankSort;
  ranked_medicines: number; // how many medicines sold in the window
  total_units: number;
  total_revenue: string;
  rows: RankedMedicine[];
}

/** The best sellers, best first; `limit` up to 100. */
export const topMedicines = (days: number, sort: MedicineRankSort, limit: number): Promise<TopMedicines> =>
  apiFetch(`/api/v1/admin/kpi/top-medicines?days=${days}&sort=${sort}&limit=${limit}`);

export interface MedicinePeriod {
  rank: number | null;
  units_sold: number;
  orders: number;
  customers: number;
  revenue: string;
}

/** One medicine's rank among the sellers — also outside the top 100 — and how it sold. */
export interface MedicineSales extends MedicinePeriod {
  window_days: number;
  period_start: string;
  period_end: string;
  previous_start: string;
  previous_end: string;
  sort: MedicineRankSort;
  medicine_id: string;
  name: string;
  brand: string | null;
  unit: string;
  mrp: string;
  active: boolean;
  ranked_medicines: number;
  unit_share: number;
  revenue_share: number;
  average_unit_price: string | null; // before GST
  lines_requested: number; // its lines in orders placed in the window
  lines_unavailable: number; // … that the pharmacy couldn't supply
  previous: MedicinePeriod;
  series_bucket: SeriesBucket;
  series: { start: string; units: number; revenue: string }[];
}

export const medicineSales = (medicineId: string, days: number, sort: MedicineRankSort): Promise<MedicineSales> =>
  apiFetch(`/api/v1/admin/kpi/medicines/${medicineId}?days=${days}&sort=${sort}`);

export type CustomerRankSort = "spend" | "orders";

export interface RankedCustomer {
  rank: number;
  user_id: string;
  name: string;
  /** The phone's last 4 digits, the rest as X. */
  phone_masked: string | null;
  orders: number;
  spend: string;
  average_order_value: string;
  last_order_at: string;
  first_order_at: string | null;
  is_new: boolean; // their first successful order was in the window
}

export interface TopCustomers {
  window_days: number;
  period_start: string;
  period_end: string;
  sort: CustomerRankSort;
  customers: number;
  total_spend: string;
  rows: RankedCustomer[];
}

export const topCustomers = (days: number, sort: CustomerRankSort, limit: number): Promise<TopCustomers> =>
  apiFetch(`/api/v1/admin/kpi/top-customers?days=${days}&sort=${sort}&limit=${limit}`);

export interface OrderQueue {
  status_code: string;
  label: string;
  count: number;
  unassigned: number; // nobody has claimed it
  stale: number; // in this status longer than stale_after_hours
  oldest_since: string | null;
  amount: string; // what the priced orders in it come to
}

/** Unfinished orders now, per status, in workflow order. */
export interface OrderQueues {
  as_of: string;
  stale_after_hours: number;
  open_orders: number;
  stale_orders: number;
  queues: OrderQueue[];
}

export const orderQueues = (): Promise<OrderQueues> => apiFetch("/api/v1/admin/kpi/order-queues");

export interface QueuedOrder {
  id: string;
  order_id: string;
  patient_name: string | null;
  assignee_name: string | null;
  final_amount: string | null;
  placed_at: string;
  in_status_since: string;
  stale: boolean;
}

export interface QueuedOrdersPage {
  status_code: string;
  label: string;
  stale_after_hours: number;
  data: QueuedOrder[]; // longest in the status first
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

export const queuedOrders = (statusCode: string, page: number, perPage: number): Promise<QueuedOrdersPage> =>
  apiFetch(`/api/v1/admin/kpi/order-queues/${encodeURIComponent(statusCode)}/orders?page=${page}&per_page=${perPage}`);

export interface DigitiseQueue {
  window_days: number;
  period_start: string;
  period_end: string;
  stale_after_hours: number;
  pending_review: number;
  in_review: number;
  stale: number;
  oldest_waiting_since: string | null;
  received: number;
  digitized: number;
  rejected: number;
  cancelled: number; // by the patient
  median_turnaround_hours: number | null;
  p90_turnaround_hours: number | null;
  rejection_reasons: { reason: string; count: number }[];
  oldest: {
    id: string;
    status: "pending_review" | "in_review";
    patient_name: string | null;
    family_member_name: string | null;
    reviewer_name: string | null;
    uploaded_at: string;
    stale: boolean;
  }[];
}

export const digitiseQueue = (days: number): Promise<DigitiseQueue> =>
  apiFetch(`/api/v1/admin/kpi/digitise-queue?days=${days}`);

// ----- Deletion review (A-14) -----

/** A deletion request as an admin reviews it: who asked, and who reviewed it (ADM-15). */
export interface AdminDeletionRequest extends DeletionRequest {
  requester_name: string | null;
  /** The phone's last 4 digits, the rest as X ("XXXXXXXXX3210"); null once purged. */
  requester_phone_masked: string | null;
  reviewed_by_name: string | null;
}

export const adminListDeletions = (status?: string): Promise<AdminDeletionRequest[]> =>
  apiFetch(`/api/v1/admin/dpdp/deletions${status ? `?status=${status}` : ""}`);

/** `note` is required to reject: the requester is shown it. */
export const adminReviewDeletion = (
  id: string,
  approve: boolean,
  note?: string,
): Promise<AdminDeletionRequest> =>
  apiFetch(`/api/v1/admin/dpdp/deletions/${id}/review`, {
    method: "POST",
    body: { approve, note: note ?? null },
  });

// ----- Request-log analytics (S-03, support-only) -----

/** Latencies are percentiles of the window's requests (SUP-09: all three were the average). */
export interface AnalyticsOverview {
  window_days: number;
  total_requests: number;
  total_errors: number;
  error_rate: number;
  p50_latency_ms: number | null;
  p95_latency_ms: number | null;
  p99_latency_ms: number | null;
}

export interface AnalyticsBreakdown {
  window_days: number;
  /** p95_latency_ms: the 95th percentile of that path's requests. */
  rows: { path_pattern: string; request_count: number; error_count: number; p95_latency_ms: number | null }[];
}

export const analyticsOverview = (days = 7): Promise<AnalyticsOverview> =>
  apiFetch(`/api/v1/support/analytics/overview?days=${days}`);

export const analyticsBreakdown = (days = 7): Promise<AnalyticsBreakdown> =>
  apiFetch(`/api/v1/support/analytics/breakdown?days=${days}`);

export const downloadAnalyticsCsv = (days = 7): Promise<void> =>
  downloadFile(`/api/v1/support/analytics/breakdown.csv?days=${days}`, "request_log_breakdown.csv");
